import React, { useState } from "react";
import {
  Calendar,
  TrendingUp,
  DollarSign,
  PieChart,
  BarChart3,
  FileText,
  Tv,
  Target,
} from "lucide-react";
import { ClientLogo } from "../../components/ClientLogo";
import type { LinearReportResponse } from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { DonutChart } from "../../components/DonutChart";
import { BarChart } from "../../components/BarChart";
import { CARD_BG, PAGE_BG, SHADOW } from "../../config/theme";
import { getChannelColor } from "../../config/colors";

interface LinearLayoutProps {
  data: LinearReportResponse;
  reportId?: string;
  clientId?: string;
}

/**
 * Dedicated layout for Linear TV advertising reports.
 * Displays channel-based performance data with spend analysis,
 * product/market breakdowns, and daily trends.
 */
export const LinearLayout: React.FC<LinearLayoutProps> = ({
  data,
  clientId,
}) => {
  const { summary, channels, daily_metrics } = data;
  const resolvedClientId = clientId ?? "ajinomoto";

  // State for table sorting
  const [sortField, setSortField] = useState<
    "channel" | "total_spend" | "record_count" | "avg_daily_spend"
  >("total_spend");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Handle column header click for sorting
  const handleSort = (
    field: "channel" | "total_spend" | "record_count" | "avg_daily_spend",
  ) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Sort channels based on current sort settings
  const sortedChannels = [...channels].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    switch (sortField) {
      case "channel":
        aValue = a.channel;
        bValue = b.channel;
        break;
      case "total_spend":
        aValue = parseFloat(a.total_spend);
        bValue = parseFloat(b.total_spend);
        break;
      case "record_count":
        aValue = a.record_count;
        bValue = b.record_count;
        break;
      case "avg_daily_spend":
        aValue = parseFloat(a.avg_daily_spend);
        bValue = parseFloat(b.avg_daily_spend);
        break;
      default:
        return 0;
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortDirection === "asc"
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  // Prepare chart data for channel distribution
  const channelDistributionData = channels.map((ch) => ({
    brand_name: ch.channel,
    units: parseFloat(ch.total_spend),
    percentage: ch.percentage_of_total,
  }));

  // Prepare data for channel comparison bar chart
  const channelLabels = channels.map((ch) => ch.channel);
  const channelSpendValues = channels.map((ch) => parseFloat(ch.total_spend));
  const channelColors = channels.map((ch) => getChannelColor(ch.channel));

  // Prepare data for daily spend trend
  const dailyLabels = daily_metrics.map((d) =>
    new Date(d.date_aired).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  );

  // Create datasets for stacked area chart (one per channel)
  const dailyDatasets = channels.map((ch) => ({
    label: ch.channel,
    data: daily_metrics.map((d) =>
      parseFloat(d.channel_breakdown[ch.channel] || "0"),
    ),
    backgroundColor: getChannelColor(ch.channel),
  }));

  return (
    <div className="py-16 px-8" data-dashboard>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Linear TV Advertising Report
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-[#B5A9AA]">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  <Tv className="w-3 h-3 mr-1" />
                  Linear TV
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {summary.date_range_start} to {summary.date_range_end}
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  {data.records_processed} records
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {data.files_processed} files processed
                </span>
              </div>
            </div>
            <div>
              <ClientLogo
                clientId={resolvedClientId}
                className="h-16 object-contain"
                fallback={
                  <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
                }
              />
            </div>
          </div>
        </div>

        {/* Key Metrics Summary */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Campaign Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Spend"
              value={`$${Math.ceil(parseFloat(summary.total_spend) / 1000).toLocaleString()}k`}
              subtitle={`Across ${summary.channel_count} channel${summary.channel_count > 1 ? "s" : ""}`}
              trend="neutral"
              icon={<DollarSign className="w-5 h-5" />}
            />
            <MetricCard
              title="Avg Daily Spend"
              value={`$${Math.ceil(parseFloat(summary.avg_daily_spend)).toLocaleString()}`}
              subtitle={`Over ${summary.total_days} days`}
              trend="neutral"
              icon={<Calendar className="w-5 h-5" />}
            />
            <MetricCard
              title="Total Records"
              value={summary.total_records.toLocaleString()}
              subtitle={`${Math.ceil(summary.total_records / summary.total_days)} per day avg`}
              trend="neutral"
              icon={<FileText className="w-5 h-5" />}
            />
            <MetricCard
              title="Active Channels"
              value={summary.channel_count.toString()}
              subtitle={summary.channels.join(", ").slice(0, 30) + "..."}
              trend="neutral"
              icon={<Target className="w-5 h-5" />}
            />
          </div>
        </div>

        {/* Charts Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Spend Distribution
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Channel Distribution Donut Chart */}
            <div
              className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Spend by Channel
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Total: $
                {parseFloat(summary.total_spend).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}
              </p>
              <DonutChart data={channelDistributionData} />
            </div>

            {/* Channel Comparison Bar Chart */}
            <div
              className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Channel Spend Comparison
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Horizontal view of total spend per channel
              </p>
              <BarChart
                labels={channelLabels}
                datasets={[
                  {
                    label: "Total Spend",
                    data: channelSpendValues,
                    backgroundColor: channelColors,
                  },
                ]}
                horizontal={true}
                showLegend={false}
              />
            </div>
          </div>
        </div>

        {/* Daily Spend Trend */}
        <div
          className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6 mb-8`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Daily Spend Trend
          </h3>
          <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
            Stacked view of spend by channel over {summary.total_days} days
          </p>
          <BarChart
            labels={dailyLabels}
            datasets={dailyDatasets}
            stacked={true}
            horizontal={false}
            showLegend={true}
          />
        </div>

        {/* Channel Detail Cards */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Tv className="w-5 h-5" />
            Channel Performance Details
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {channels.map((channel) => (
              <div
                key={channel.channel}
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {channel.channel}
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {channel.percentage_of_total.toFixed(1)}%
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-[#B5A9AA]">
                      Total Spend
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      $
                      {parseFloat(channel.total_spend).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-[#B5A9AA]">
                      Avg Daily
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      $
                      {parseFloat(channel.avg_daily_spend).toLocaleString(
                        "en-US",
                        { minimumFractionDigits: 2 },
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-600 dark:text-[#B5A9AA]">
                      Records
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {channel.record_count.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 dark:text-[#B5A9AA]">
                      Days Active
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {channel.days_with_data}
                    </div>
                  </div>
                </div>

                {/* Product Breakdown (Golf Channel & PGA Tour) */}
                {channel.spend_by_product &&
                  Object.keys(channel.spend_by_product).length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-[#5B5353] mb-4">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Spend by Product
                      </div>
                      <div className="space-y-1">
                        {Object.entries(channel.spend_by_product)
                          .sort(([, a], [, b]) => parseFloat(b) - parseFloat(a))
                          .map(([product, spend]) => (
                            <div
                              key={product}
                              className="flex justify-between text-xs"
                            >
                              <span className="text-gray-600 dark:text-[#B5A9AA] truncate">
                                {product}
                              </span>
                              <span className="text-gray-900 dark:text-white font-medium ml-2">
                                $
                                {parseFloat(spend).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Market Breakdown (Franchises TV OTT) */}
                {channel.spend_by_market &&
                  Object.keys(channel.spend_by_market).length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-[#5B5353] mb-4">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Spend by Market
                      </div>
                      <div className="space-y-1">
                        {Object.entries(channel.spend_by_market)
                          .sort(([, a], [, b]) => parseFloat(b) - parseFloat(a))
                          .slice(0, 5) // Show top 5 markets
                          .map(([market, spend]) => (
                            <div
                              key={market}
                              className="flex justify-between text-xs"
                            >
                              <span className="text-gray-600 dark:text-[#B5A9AA] truncate">
                                {market}
                              </span>
                              <span className="text-gray-900 dark:text-white font-medium ml-2">
                                $
                                {parseFloat(spend).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          ))}
                        {Object.keys(channel.spend_by_market).length > 5 && (
                          <div className="text-xs text-gray-500 dark:text-[#B5A9AA] italic">
                            +{Object.keys(channel.spend_by_market).length - 5}{" "}
                            more markets
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Status Breakdown */}
                {Object.keys(channel.spend_by_status).length > 0 && (
                  <div className="pt-4 border-t border-gray-200 dark:border-[#5B5353]">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Spend by Status
                    </div>
                    <div className="space-y-1">
                      {Object.entries(channel.spend_by_status)
                        .sort(([, a], [, b]) => parseFloat(b) - parseFloat(a))
                        .map(([status, spend]) => (
                          <div
                            key={status}
                            className="flex justify-between text-xs"
                          >
                            <span className="text-gray-600 dark:text-[#B5A9AA]">
                              {status}
                            </span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              $
                              {parseFloat(spend).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Data Table */}
        <div
          className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Channel Summary Table
          </h3>
          <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
            Sortable view of all channel performance metrics
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th
                    onClick={() => handleSort("channel")}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Channel{" "}
                    {sortField === "channel" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("total_spend")}
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Total Spend{" "}
                    {sortField === "total_spend" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("avg_daily_spend")}
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Avg Daily{" "}
                    {sortField === "avg_daily_spend" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("record_count")}
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Records{" "}
                    {sortField === "record_count" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                    % of Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                    Date Range
                  </th>
                </tr>
              </thead>
              <tbody
                className={`${CARD_BG} divide-y divide-gray-200 dark:divide-gray-700`}
              >
                {sortedChannels.map((channel) => (
                  <tr
                    key={channel.channel}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {channel.channel}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                      $
                      {parseFloat(channel.total_spend).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                      $
                      {parseFloat(channel.avg_daily_spend).toLocaleString(
                        "en-US",
                        { minimumFractionDigits: 2 },
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-[#B5A9AA]">
                      {channel.record_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {channel.percentage_of_total.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-[#B5A9AA]">
                      {channel.date_range_start} → {channel.date_range_end}
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        {channel.days_with_data} days
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
